import * as React from "react";
import styled from "@emotion/styled";
import FormBlock from "@/components/molecules/FormBlock";
import PropertyType from "@/constants/PropertyType";
import classNames from "classnames";
import FormContainer from "@/components/atoms/FormContainer";
import Title from "@/components/atoms/Title";
import NextButton from "@/components/molecules/NextButton";
import CapsuleNextButton from "@/components/molecules/CapsuleNextButton";
import Article from "@/components/molecules/Article";
import MiniTitle from "@/components/atoms/MiniTitle";
import withEffect, { InjectProps } from "./effect";
import { range } from "@/utils/number";
import { toIncludeJapaneseCalendar } from "@/utils/date";
import { validateProperty as validator } from "@/utils/validation";
import { useFormHandler } from "@/utils/hooks";
import { isBlank } from "@/utils/is";
import contract, { HIDDEN_COMPONENT_OPTIONS, isHiddenComponent, Feature } from "@/utils/contract";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import { useTheme } from "@/hooks/useTheme";
import infoIcon from "@/assets/icons/info-icon.svg";
import breakpoint from "@/utils/breakpoint";
import { structureOptions } from "@/constants/Structure";

const WarningText = styled.p`
  color: #bd5405;
  display: inline-block;
  font-weight: bold;
  font-size: 11pt;
  padding-left: 1em;
`;

const StyledDesc = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: start;
  ${breakpoint.lessThan("sm")`
  flex-direction: column;
  align-items: left;
`}
`;

const StyledWarn = styled.div`
  font-size: 12px;
  letter-spacing: 0px;
  font-weight: 400;
  color: #333333;
  align-self: flex-end;
  margin-bottom: 15px;
  ${breakpoint.lessThan("sm")`
  margin-left: 30px;
  align-self: flex-start;
`}
`;

const Span = styled.span`
  font-size: 12px;
  letter-spacing: 0px;
  color: #333333;
  font-weight: 400;
  opacity: 1;
  text-align: left;
`;

const Alignment = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 5px;
`;
export type PropertyBlockProps = {
  onNext: () => void;
};

const toWarekiForAD = (ad: number) => {
  // nullの場合は意図的にエラーにする、和暦だけ欲しいので令和1年である5/1に合わせる
  return (toIncludeJapaneseCalendar(`${ad}-01`, true) as string).replace(/\d{1,2}月$/g, "");
};

const years: Option[] = range(1925, new Date().getFullYear() + 1).map(year => ({
  label: toWarekiForAD(year),
  value: `${year}`,
}));

const months: Option[] = range(1, 13).map(month => ({
  label: `${month}月`,
  value: `00${month}`.slice(-2),
}));

const PropertyBlock = ({
  values,
  validates,
  addressIds,
  onNext,
  prefs,
  cities,
  towns,
  lines,
  stations,
  isValid,
  clearState,
  selectMansion,
  onManualValueTriggered,
}: PropertyBlockProps & InjectProps) => {
  const [busField, setBusField] = React.useState(false);
  const { COLOR_THEME, isNewTheme } = useTheme();
  const firstRef = React.useRef(true);

  const {
    state: { featuresObj },
  } = React.useContext(ContractContext) || initContractStore(contract);

  const [showNextButton, setShowNextButton] = React.useState(true);
  const [linesInfo, setLinesInfo] = React.useState<{ label: string; value: string } | null>(null);
  const [stationInfo, setStationInfo] = React.useState<{ label: string; value: string } | null>(null);
  const [prefInfo, setPrefInfo] = React.useState<{ label: string; value: string } | null>(null);
  const [propertyCityInfo, setPropertyCityInfo] = React.useState<{ label: string; value: string } | null>(null);
  const [propertyTown, setPropertyTown] = React.useState<{ label: string; value: string } | null>(null);

  React.useEffect(() => {
    if (values.station) {
      setStationInfo({
        label: values.station,
        value: values.station,
      });
    }
  }, [values.station]);

  React.useEffect(() => {
    if (values.line) {
      setLinesInfo({
        label: values.line,
        value: values.line,
      });
    }
  }, [values.line]);

  React.useEffect(() => {
    const currentPrefExist = prefs.find(pref => pref.value === values.propertyPref);
    if (values.propertyPref && currentPrefExist?.value) {
      setPrefInfo({
        label: values.propertyPref,
        value: values.propertyPref,
      });
    } else {
      setPrefInfo(null);
    }
  }, [values.propertyPref, prefs]);

  React.useEffect(() => {
    const currentCityExist = cities.find(city => city.value === values.propertyCity);
    if (values.propertyCity && currentCityExist?.value) {
      setPropertyCityInfo({
        label: values.propertyCity,
        value: values.propertyCity,
      });
    } else {
      setPropertyCityInfo(null);
    }
  }, [values.propertyCity, cities]);

  React.useEffect(() => {
    const currentTownExist = towns.find(town => town.value === values.propertyTown);
    if (values.propertyTown && currentTownExist?.value) {
      setPropertyTown({
        label: values.propertyTown,
        value: values.propertyTown,
      });
    } else {
      setPropertyTown(null);
    }
  }, [values.propertyTown, towns]);

  const handlePropertyName = useFormHandler("propertyName", validator.name);
  const handlePropertyPref = useFormHandler("propertyPref", validator.pref);
  const handlePropertyCity = useFormHandler("propertyCity", validator.city);
  const handlePropertyTown = useFormHandler("propertyTown", validator.town);
  const handlePropertyStreet = useFormHandler("propertyStreet", validator.street);
  const handleLine = useFormHandler("line", validator.line);
  const handleStation = useFormHandler("station", validator.station);
  const handleBus = useFormHandler("bus", validator.bus);
  const handleWalk = useFormHandler("walk", validator.walk);
  const handleBuiltDateYear = useFormHandler("builtDateYear", validator.builtDateYear);
  const handleBuiltDateMonth = useFormHandler("builtDateMonth", validator.builtDateMonth);
  const handleIsSame = useFormHandler("isSame");
  const handleTotalStory = useFormHandler("totalStory", validator.totalStory);
  const handleStructure = useFormHandler("structure", validator.structure);

  const handleChangePref = (ev: React.ChangeEvent<HTMLSelectElement> & { label: string; value: string }) => {
    onManualValueTriggered();
    if (ev && typeof ev == "object" && ev.label && ev.value) {
      setPrefInfo(ev as { label: string; value: string });
      handlePropertyPref(ev.value);
    } else {
      handlePropertyPref(ev.target.value);
    }
    if (values.propertyType === PropertyType.MANSION) {
      clearState("propertyCity", "propertyTown");
    } else {
      clearState("propertyCity", "propertyTown", "line", "station");
    }
  };

  const handleChangeCity = (ev: React.ChangeEvent<HTMLSelectElement> & { label: string; value: string }) => {
    onManualValueTriggered();
    if (ev && typeof ev == "object" && ev.label && ev.value) {
      setPropertyCityInfo(ev as { label: string; value: string });
      handlePropertyCity(ev.value);
    } else {
      handlePropertyCity(ev.target.value);
    }
    clearState("propertyTown");
  };

  const handleChangeLine = (ev: React.ChangeEvent<HTMLSelectElement> & { label: string; value: string }) => {
    if (ev && typeof ev == "object" && ev.label && ev.value) {
      setLinesInfo(ev as { label: string; value: string });
      handleLine(ev.value);
    } else {
      handleLine(ev.target.value);
    }
    if (values.propertyType === PropertyType.MANSION) {
      clearState("station");
    }
  };

  const handleNext = () => {
    firstRef.current = false;
    onNext();
  };

  const isMansion = PropertyType.MANSION === values.propertyType;
  const isLand = PropertyType.LAND === values.propertyType;
  const hasAllPrefectures = !prefs.length || prefs.length === 47;
  const hiddenLivingAddressComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);
  const isTrafficInfoAutoLinked = featuresObj[Feature.TRAFFIC_INFO_AUTO_LINK]?.enabled;
  const hiddenTotalStoryComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.TOTAL_STORY);
  const hiddenStructureComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.STRUCTURE);

  React.useEffect(() => {
    if (hiddenTotalStoryComponent) {
      values.totalStory = "";
    }
  }, [hiddenTotalStoryComponent]);

  React.useEffect(() => {
    if (hiddenStructureComponent) {
      values.structure = "";
    }
  }, [hiddenStructureComponent]);

  const renderMiniTitle = () => {
    return (
      <StyledDesc>
        <Title color={COLOR_THEME.extras?.secondary}>査定物件についてご入力ください</Title>
        {!hasAllPrefectures && <StyledWarn>※ 一部お取り扱いのない地域がございます</StyledWarn>}
      </StyledDesc>
    );
  };

  const renderNextButton = () => {
    return (
      <React.Fragment>
        {!isNewTheme ? (
          <NextButton disabled={firstRef.current && !isValid} value="次へ進む" onClick={handleNext} />
        ) : (
          <CapsuleNextButton
            disabled={firstRef.current && !isValid}
            value="次へ"
            className="formBtn"
            onClick={() => {
              setShowNextButton(false);
              handleNext();
            }}
          />
        )}
      </React.Fragment>
    );
  };

  const formBlockProps = {
    className: `${isNewTheme ? "form" : ""}`,
    backgroundColor: "#ffffff",
  };

  const isCssOverride = classNames({
    override: isNewTheme,
  });

  const oldPropertyBlockFields = {
    apartmentName: {
      description: "マンション検索をすると、物件情報を一部自動で入力できます",
    },
    access: {
      label: "交通アクセス",
    },
  };
  const newPropertyBlockFields = {
    apartmentName: {
      description: (
        <Alignment>
          <StyledIcon src={infoIcon} height="14" width="14" />
          <Span>マンション検索をすると、物件情報を自動入力できます</Span>
        </Alignment>
      ),
    },
    access: {
      label: "最寄駅",
    },
  };

  const hideStreetAddress = () => {
    if (isTrafficInfoAutoLinked) {
      return false;
    } else {
      if (isMansion) {
        return true;
      } else {
        return false;
      }
    }
  };

  const propertyBlockFields = isNewTheme ? newPropertyBlockFields : oldPropertyBlockFields;
  const renderAccessInfo = () => {
    if (isNewTheme) {
      return (
        <React.Fragment>
          <FormBlock.Row>
            <FormBlock.Checkbox2
              value={busField ? "on" : "off"}
              name="canContactTel"
              onChange={ev => {
                if (ev.target.value === "on") {
                  handleBus("");
                }
                setBusField(!busField);
              }}
              checked={busField}
              text="最寄駅までバスを利用する"
              disabled={false}
              labelFontSize="14px"
              labelfontWeight="400"
            />
          </FormBlock.Row>
          <FormBlock.Row className={isNewTheme ? "displayRow" : ""}>
            <FormBlock.Input
              description="　"
              before="徒歩"
              after="分"
              aria-label="徒歩"
              value={values.walk}
              placeholder="例）5"
              onChange={ev => handleWalk(ev.target.value)}
              width="80px"
              className={classNames({
                inputCss3: isNewTheme,
              })}
              inputWrapperClassName="fieldWidth"
              validateMessage={validates.walk}
              required
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
            {busField && (
              <FormBlock.Input
                description="　"
                before="バス"
                after="分"
                aria-label="バス"
                value={values.bus}
                placeholder="例）10"
                onChange={ev => handleBus(ev.target.value)}
                width="80px"
                inputWrapperClassName="fieldWidth"
                className={classNames({
                  inputCss3: isNewTheme,
                })}
                validateMessage={validates.bus}
                background={COLOR_THEME.extras?.invalid_state?.background}
              />
            )}
          </FormBlock.Row>
        </React.Fragment>
      );
    }
    return (
      <FormBlock.Row>
        <FormBlock.Input
          description="駅から "
          before="バス"
          after="分"
          aria-label="バス"
          value={values.bus}
          placeholder="例）10"
          onChange={ev => handleBus(ev.target.value)}
          width="20%"
          validateMessage={validates.bus}
          background={COLOR_THEME.extras?.invalid_state?.background}
        />
        <FormBlock.Input
          description="　"
          before="徒歩"
          after="分"
          aria-label="徒歩"
          value={values.walk}
          placeholder="例）5"
          onChange={ev => handleWalk(ev.target.value)}
          width="20%"
          validateMessage={validates.walk}
          required
          background={COLOR_THEME.extras?.invalid_state?.background}
        />
      </FormBlock.Row>
    );
  };

  return (
    <Article>
      {isNewTheme ? (
        renderMiniTitle()
      ) : (
        <Article.Header>
          <MiniTitle>売却物件についてお聞かせください</MiniTitle>
          {!hasAllPrefectures && <WarningText>※一部、お取り扱いのない地域がございます。ご了承ください。</WarningText>}
        </Article.Header>
      )}
      <Article.Block className={isNewTheme ? "border-none bgWhite" : ""}>
        {isMansion && (
          <FormContainer className={isCssOverride}>
            <FormBlock {...formBlockProps}>
              <FormBlock.Label
                value="マンション名"
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
              />
              <FormBlock.Row>
                <FormBlock.Input
                  description={propertyBlockFields.apartmentName.description as string}
                  isInfoText={true}
                  placeholder="例）SREマンション"
                  value={values.propertyName}
                  onChange={ev => {
                    onManualValueTriggered();
                    handlePropertyName(ev.target.value);
                  }}
                  aria-label="査定物件のマンション名"
                  width="70%"
                  className={classNames({
                    inputCss: isNewTheme,
                  })}
                  search
                  validateMessage={validates.propertyName}
                  required
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  selectMansion={selectMansion}
                />
              </FormBlock.Row>
            </FormBlock>
          </FormContainer>
        )}
        <FormContainer className={isCssOverride}>
          <FormBlock {...formBlockProps}>
            <FormBlock.Label
              required
              value="所在地"
              background={COLOR_THEME.extras?.required_icon?.background}
              text={COLOR_THEME.extras?.required_icon?.text}
              border={COLOR_THEME.extras?.required_icon?.border}
            />
            <FormBlock.Row>
              {!isNewTheme ? (
                <FormBlock.Select
                  description="都道府県"
                  value={values.propertyPref}
                  options={prefs}
                  onChange={handleChangePref}
                  width="40%"
                  className={classNames({
                    selectCss: isNewTheme,
                  })}
                  validateMessage={validates.propertyPref}
                  required
                  aria-label="査定物件の都道府県"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              ) : (
                <FormBlock.ReactSelect
                  description="都道府県"
                  aria-label="査定物件の都道府県"
                  width="200px"
                  height="40px"
                  options={prefs}
                  value={prefInfo}
                  isSearchable={true}
                  onChange={handleChangePref}
                  required
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  validateMessage={validates.propertyPref}
                  placeholder="選択してください"
                />
              )}
            </FormBlock.Row>
            <FormBlock.Row>
              {!isNewTheme ? (
                <FormBlock.Select
                  disabled={cities.length <= 0}
                  description="市区町村"
                  value={values.propertyCity && addressIds.cityId ? values.propertyCity : ""}
                  options={cities}
                  onChange={handleChangeCity}
                  width="40%"
                  className={classNames({
                    selectCss: isNewTheme,
                  })}
                  validateMessage={validates.propertyCity}
                  required
                  aria-label="査定物件の市区町村"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              ) : (
                <FormBlock.ReactSelect
                  description="市区町村"
                  aria-label="査定物件の市区町村"
                  width="200px"
                  height="40px"
                  options={cities}
                  value={values.propertyCity && addressIds.cityId ? propertyCityInfo : ""}
                  isSearchable={true}
                  onChange={handleChangeCity}
                  required
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  validateMessage={validates.propertyCity}
                  placeholder="選択してください"
                  isDisabled={cities.length <= 0}
                />
              )}
            </FormBlock.Row>
            <FormBlock.Row>
              {!isNewTheme ? (
                <FormBlock.Select
                  disabled={towns.length <= 0}
                  description="町丁目"
                  value={values.propertyTown && addressIds.townId ? values.propertyTown : ""}
                  options={towns}
                  onChange={ev => {
                    onManualValueTriggered();
                    handlePropertyTown(ev.target.value);
                  }}
                  width="40%"
                  className={classNames({
                    selectCss: isNewTheme,
                  })}
                  validateMessage={validates.propertyTown}
                  required
                  aria-label="査定物件の町丁目"
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              ) : (
                <FormBlock.ReactSelect
                  description="町丁目"
                  aria-label="査定物件の町丁目"
                  width="200px"
                  height="40px"
                  options={towns}
                  value={values.propertyTown && addressIds.townId ? propertyTown : ""}
                  isSearchable={true}
                  onChange={(ev: any) => {
                    onManualValueTriggered();
                    setPropertyTown(ev);
                    handlePropertyTown(ev.value);
                  }}
                  required
                  background={COLOR_THEME.extras?.invalid_state?.background}
                  validateMessage={validates.propertyTown}
                  placeholder="選択してください"
                  isDisabled={towns.length <= 0}
                />
              )}
            </FormBlock.Row>
            <FormBlock.Row hidden={hideStreetAddress()}>
              <FormBlock.Input
                disabled={isBlank(values.propertyCity)}
                description="番地"
                placeholder="例）1-1"
                value={values.propertyStreet}
                onChange={ev => handlePropertyStreet(ev.target.value)}
                width={isNewTheme ? "200px" : "40%"}
                className={classNames({
                  inputCss2: isNewTheme,
                })}
                validateMessage={validates.propertyStreet}
                required
                aria-label="査定物件の番地"
                background={COLOR_THEME.extras?.invalid_state?.background}
              />
            </FormBlock.Row>
            <FormBlock.Row hidden={isNewTheme ? true : hiddenLivingAddressComponent}>
              <FormBlock.Radio
                className="override"
                name="currentaddress"
                description="この住所にお住いですか？"
                value={values.isSame ? "はい" : "いいえ"}
                values={["はい", "いいえ"]}
                width="40%"
                inline
                onChangeRadio={value => handleIsSame(value === "はい")}
                aria-label="この住所にお住いですか？"
              />
            </FormBlock.Row>
          </FormBlock>
        </FormContainer>
        {!isTrafficInfoAutoLinked && (
          <FormContainer className={isCssOverride}>
            <FormBlock {...formBlockProps}>
              <FormBlock.Label
                value={propertyBlockFields.access.label}
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
              />
              <FormBlock.Row>
                {isMansion ? (
                  <>
                    <FormBlock.Input
                      description="路線名"
                      aria-label="路線名"
                      value={values.line}
                      onChange={ev => handleLine(ev.target.value)}
                      width={isNewTheme ? "200px" : "40%"}
                      className={classNames({
                        inputCss2: isNewTheme,
                      })}
                      validateMessage={validates.line}
                      required
                      placeholder="例）JR山手線"
                      background={COLOR_THEME.extras?.invalid_state?.background}
                    />
                    <FormBlock.Input
                      description="駅名"
                      aria-label="駅名"
                      value={values.station}
                      onChange={ev => handleStation(ev.target.value)}
                      width="40%"
                      className={classNames({
                        inputCss2: isNewTheme,
                      })}
                      validateMessage={validates.station}
                      required
                      placeholder="例）日本橋"
                      background={COLOR_THEME.extras?.invalid_state?.background}
                    />
                  </>
                ) : (
                  <>
                    {isNewTheme ? (
                      <>
                        <FormBlock.ReactSelect
                          description="路線名"
                          aria-label="路線名"
                          width="200px"
                          height="40px"
                          options={lines}
                          value={linesInfo}
                          isSearchable={true}
                          onChange={handleChangeLine}
                          isDisabled={lines.length <= 0}
                          required
                          background={COLOR_THEME.extras?.invalid_state?.background}
                          validateMessage={validates.line}
                          placeholder="選択してください"
                        />
                        <FormBlock.ReactSelect
                          description="駅名"
                          aria-label="駅名"
                          options={stations}
                          value={stationInfo}
                          onChange={(ev: any) => {
                            setStationInfo(ev);
                            handleStation(ev.value);
                          }}
                          validateMessage={validates.station}
                          required
                          background={COLOR_THEME.extras?.invalid_state?.background}
                          isDisabled={stations.length <= 0}
                          width="200px"
                          height="40px"
                          isSearchable={true}
                          placeholder="選択してください"
                        />
                      </>
                    ) : (
                      <>
                        <FormBlock.Select
                          disabled={lines.length <= 0}
                          description="路線名"
                          aria-label="路線名"
                          options={lines}
                          value={values.line}
                          onChange={handleChangeLine}
                          width={isNewTheme ? "200px" : "40%"}
                          className={classNames({
                            selectCss: isNewTheme,
                          })}
                          validateMessage={validates.line}
                          required
                          background={COLOR_THEME.extras?.invalid_state?.background}
                        />
                        <FormBlock.Select
                          disabled={stations.length <= 0}
                          description="駅名"
                          aria-label="駅名"
                          options={stations}
                          value={values.station}
                          onChange={ev => handleStation(ev.target.value)}
                          width={isNewTheme ? "200px" : "40%"}
                          className={classNames({
                            selectCss: isNewTheme,
                          })}
                          validateMessage={validates.station}
                          required
                          background={COLOR_THEME.extras?.invalid_state?.background}
                        />
                      </>
                    )}
                  </>
                )}
              </FormBlock.Row>
              {renderAccessInfo()}
            </FormBlock>
          </FormContainer>
        )}
        {!isLand && (
          <FormContainer className={isCssOverride}>
            <FormBlock {...formBlockProps}>
              <FormBlock.Label
                value="築年月"
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
              />
              <FormBlock.Row className={isNewTheme ? "displayRow flexStart" : ""}>
                <FormBlock.Select
                  value={values.builtDateYear}
                  onChange={ev => handleBuiltDateYear(ev.target.value)}
                  options={years}
                  aria-label="築年月"
                  width={isNewTheme ? "200px" : "40%"}
                  className={classNames({
                    selectCss: isNewTheme,
                    noPadding: isNewTheme,
                  })}
                  wrapperClassName={classNames({
                    marginLeft10: isNewTheme,
                  })}
                  validateMessage={validates.builtDateYear}
                  required
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
                <FormBlock.Select
                  value={values.builtDateMonth}
                  onChange={ev => handleBuiltDateMonth(ev.target.value)}
                  options={months}
                  aria-label="築年月"
                  width={isNewTheme ? "108px" : "40%"}
                  className={classNames({
                    selectCss2: isNewTheme,
                  })}
                  validateMessage={validates.builtDateMonth}
                  required
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
            </FormBlock>
          </FormContainer>
        )}
        {isMansion && (
          <FormContainer className={isCssOverride}>
            {!hiddenTotalStoryComponent && (
              <FormBlock {...formBlockProps}>
                <FormBlock.Label
                  value="総階数"
                  required
                  background={COLOR_THEME.extras?.required_icon?.background}
                  text={COLOR_THEME.extras?.required_icon?.text}
                  border={COLOR_THEME.extras?.required_icon?.border}
                ></FormBlock.Label>
                <FormBlock.Row>
                  <FormBlock.Input
                    before="総階数"
                    after="階"
                    aria-label="totalStory"
                    value={values.totalStory}
                    placeholder="10"
                    onChange={ev => handleTotalStory(ev.target.value)}
                    width="80px"
                    className={classNames({
                      inputCss3: isNewTheme,
                    })}
                    inputWrapperClassName="fieldWidth"
                    validateMessage={validates.totalStory}
                    required
                    background={COLOR_THEME.extras?.invalid_state?.background}
                  />
                </FormBlock.Row>
              </FormBlock>
            )}
            {!hiddenStructureComponent && (
              <FormBlock {...formBlockProps}>
                <FormBlock.Label
                  value="建物構造"
                  required
                  background={COLOR_THEME.extras?.required_icon?.background}
                  text={COLOR_THEME.extras?.required_icon?.text}
                  border={COLOR_THEME.extras?.required_icon?.border}
                />
                <FormBlock.Row>
                  <FormBlock.Select
                    description="建物構造"
                    aria-label="建物構造"
                    options={structureOptions}
                    onChange={ev => handleStructure(ev.target.value)}
                    width="200px"
                    className="width200px"
                    value={hiddenStructureComponent ? "" : values.structure}
                    validateMessage={validates.structure}
                    required
                    background={COLOR_THEME.extras?.invalid_state?.background}
                  />
                </FormBlock.Row>
              </FormBlock>
            )}
          </FormContainer>
        )}
      </Article.Block>
      {showNextButton && renderNextButton()}
    </Article>
  );
};

export default withEffect(PropertyBlock);
